// features/permissions.js

import { compareModules } from "./sidebar-order";

export const PERMISSIONS = [
  { key: "can_full", label: "Full", hint: "Every action below on this module" },
  { key: "can_view", label: "View", hint: "Module shows in the sidebar and can be opened" },
  { key: "can_add", label: "Create", hint: "Can add new records" },
  { key: "can_edit", label: "Edit", hint: "Can change existing records" },
  { key: "can_delete", label: "Delete", hint: "Can remove records" },
];

export const PERMISSION_KEYS = PERMISSIONS.map((p) => p.key);

export const EMPTY_PERMISSIONS = { can_view: false, can_add: false, can_edit: false, can_delete: false, can_full: false };

// Same rules the server enforces: Full means every action, and any action needs View.
export const applyToggle = (current, key, value) => {
  const next = { ...EMPTY_PERMISSIONS, ...current, [key]: value };
  if (key === "can_full") {
    next.can_add = value;
    next.can_edit = value;
    next.can_delete = value;
    if (value) next.can_view = true;
  } else if (key === "can_view") {
    if (!value) {
      next.can_add = false;
      next.can_edit = false;
      next.can_delete = false;
    }
  } else if (value) {
    next.can_view = true;
  }
  next.can_full = next.can_view && next.can_add && next.can_edit && next.can_delete;
  return next;
};

export const isSamePermissions = (a, b) => PERMISSION_KEYS.every((k) => Boolean(a?.[k]) === Boolean(b?.[k]));

export const toPermissionState = (modules, sourceModules = modules) => {
  const sourceById = Object.fromEntries(sourceModules.map((m) => [m.id, m.permissions]));
  return Object.fromEntries(modules.map((m) => [m.id, { ...EMPTY_PERMISSIONS, ...(sourceById[m.id] || {}) }]));
};

// Assigned modules come first in the role's sidebar order, then the rest by name.
export const buildIndex = (modules) => {
  const byId = {};
  const childrenById = {};
  const roots = [];
  const sorted = [...modules].sort(compareModules);
  sorted.forEach((m) => {
    byId[m.id] = m;
    childrenById[m.id] = [];
  });
  sorted.forEach((m) => {
    if (m.parent_fid && byId[m.parent_fid]) childrenById[m.parent_fid].push(m.id);
    else roots.push(m.id);
  });
  return { byId, childrenById, roots };
};

// A sub-module is reachable only through its parent menu: granting a child turns the
// parent's View on, and removing a parent's View clears all of its children.
export const setPermissions = (state, ids, key, value, index) => {
  const next = { ...state };
  ids.forEach((id) => {
    next[id] = applyToggle(next[id], key, value);
  });
  ids.forEach((id) => {
    if (next[id].can_view) {
      const parentId = index.byId[id]?.parent_fid;
      if (parentId && index.byId[parentId] && !next[parentId]?.can_view) {
        next[parentId] = applyToggle(next[parentId], "can_view", true);
      }
    } else {
      index.childrenById[id]?.forEach((childId) => {
        next[childId] = { ...EMPTY_PERMISSIONS };
      });
    }
  });
  return next;
};

export const groupState = (state, ids, key) => {
  if (!ids.length) return "off";
  const on = ids.reduce((count, id) => count + (state[id]?.[key] ? 1 : 0), 0);
  if (on === 0) return "off";
  return on === ids.length ? "on" : "mixed";
};

export const toPayload = (state) =>
  Object.entries(state)
    .filter(([, p]) => p.can_view)
    .map(([id, p]) => ({ module_master_fid: Number(id), ...p }));
